
import React, { useContext, useState, useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { Theme } from "@rjsf/bootstrap-4";
import { withTheme, utils } from "@rjsf/core";
import { getCokkie } from "../../../login/Login";
import { Badge, Button, Progress } from "reactstrap";
import { alertContext } from "../../../../components/modal/ModalComponent";
import useFetch from "../../../../components/useFetch";
import Loader from "../../../../components/Loader";
import DataConfiguration2 from "../../../../components/modal/NavPanes/DatConfiguration2";
import ErrorScreen from "../../../../components/error/ErrorScreen";
import { Upload } from "@aws-sdk/lib-storage";
import { S3Client } from "@aws-sdk/client-s3";
import { CustomWidget } from "../../../../components/widgets/CustomWidgets";
import { Dropzone, FileMosaic } from "@files-ui/react";
import VideoRecorder from "./VideoRecorder";
import JSZip from "jszip";
import '../AddDataset.scss'
import LabelChipsInput, { isLabelsPromptField } from "../LabelChipsInput";
import {
  formatDuplicateLabelsError,
  getDuplicateLabelNamesInSchema,
  validateDuplicateLabelsInSchema,
} from "../labelValidationUtils";
import {
  isEnabledSelection,
  mergeReviewDataWithEditableData,
  normalizeSchemaFormData,
  sanitizeDatasetWizardData,
} from "../schemaFormDataUtils";
import {
  unlabeledRjsfWidgets,
  LabelsPromptArrayField,
  LabelsPromptEditor,
  isNamePromptLabelArray,
} from "../DatasetFormUI";
import { AddDatasetIcon, ArrowRightIcon, BackIcon, CheckCircleIcon, CloseIcon, DatasetIcon, NetworkIcon, PlatformIcon, UploadIcon } from "../../../Common/SvgIcons/WorkflowIcons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faFileZipper, faSpinner, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { CheckIcon } from "lucide-react";

const S3_BUCKET = `${window.ENV.AWS_DATASET_UPLOAD}`;
const REGION = `${window.ENV.AWS_REGION}`;
const formatInlineValidationError = (message = "") =>
  String(message || "")
    .replace(/\s*\(items ## \d+ and \d+ are identical\)/gi, "")
    .trim();

// Initialize Form with theme
const registry = utils.getDefaultRegistry();
const defaultFileWidget = registry.widgets["FileWidget"];
const defaultRangeWidget = registry.widgets["RangeWidget"];

Theme.widgets = {
  ...Theme.widgets,
  FileWidget: defaultFileWidget,
  RangeWidget: defaultRangeWidget
};

const Form = withTheme(Theme);

const SUPPORTED_IMAGE_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.webp', '.tif', '.tiff', '.bmp'];
const SUPPORTED_VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v', '.mpeg', '.mpg'];
const SUPPORTED_MEDIA_EXTENSIONS = [
  ...SUPPORTED_IMAGE_EXTENSIONS,
  ...SUPPORTED_VIDEO_EXTENSIONS,
];
const MAX_FILE_PREVIEWS = 50;

const hasSupportedMediaExtension = (file) => {
  const fileName = file?.name?.toLowerCase() || "";
  return SUPPORTED_MEDIA_EXTENSIONS.some((extension) => fileName.endsWith(extension));
};

const isVideoFile = (file) => {
  const fileName = file?.name?.toLowerCase() || "";
  return (
    file?.type?.startsWith("video/") ||
    SUPPORTED_VIDEO_EXTENSIONS.some((extension) => fileName.endsWith(extension))
  );
};

const canPreviewMediaFile = (file) =>
  file?.type?.startsWith("image/") ||
  file?.type?.startsWith("video/") ||
  hasSupportedMediaExtension(file);

const FileWidget = ({ onChange, formData, uploading, uploadProgress, onUpload, formContext }) => {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [previewPage, setPreviewPage] = useState(0);
  const initialFilesAppliedRef = useRef(false);
  const filesRef = useRef([]);
  const nextFileIdRef = useRef(0);
  const pendingImageFilesRef = useRef(null);
  const zipGenerationRunningRef = useRef(false);
  const zipGenerationVersionRef = useRef(0);
  const previewContainerRef = useRef(null);
  const selectedUploadType = String(
    formContext?.formData?.["Upload Data Type"] || ""
  ).toLowerCase();
  const isVideoUpload = selectedUploadType.includes("video");
  const isImageUpload = selectedUploadType.includes("image");
  const allowedMediaExtensions = isVideoUpload
    ? SUPPORTED_VIDEO_EXTENSIONS
    : isImageUpload
      ? SUPPORTED_IMAGE_EXTENSIONS
      : SUPPORTED_MEDIA_EXTENSIONS;
  const acceptedFileTypes = [
    ".zip",
    ...allowedMediaExtensions,
    ...(isVideoUpload
      ? ["video/mp4", "video/webm", "video/quicktime", "video/x-msvideo", "video/x-matroska", "video/mpeg"]
      : isImageUpload
        ? ["image/jpeg", "image/png", "image/webp", "image/tiff", "image/bmp"]
        : ["image/*", "video/*"]),
  ].join(",");

  const isAllowedMediaFile = (file) => {
    const fileName = file?.name?.toLowerCase() || "";
    return allowedMediaExtensions.some((extension) => fileName.endsWith(extension));
  };
  const selectedUploadTypeRef = useRef(selectedUploadType);

  const revokeFilePreviews = (fileWrappers) => {
    fileWrappers.forEach((fileWrapper) => {
      if (fileWrapper.preview) URL.revokeObjectURL(fileWrapper.preview);
    });
  };

  const replaceFiles = (nextFiles) => {
    revokeFilePreviews(filesRef.current);
    filesRef.current = nextFiles;
    setFiles(nextFiles);
  };

  const activateVisiblePreviews = (fileWrappers, startIndex = 0) => {
    const endIndex = startIndex + MAX_FILE_PREVIEWS;

    fileWrappers.forEach((fileWrapper, index) => {
      const isVisible = index >= startIndex && index < endIndex;

      if (!isVisible && fileWrapper.preview) {
        URL.revokeObjectURL(fileWrapper.preview);
        fileWrapper.preview = null;
      } else if (isVisible && !fileWrapper.preview && canPreviewMediaFile(fileWrapper.file)) {
        fileWrapper.preview = URL.createObjectURL(fileWrapper.file);
      }
    });
  };

  useEffect(() => () => {
    revokeFilePreviews(filesRef.current);
    zipGenerationVersionRef.current += 1;
    pendingImageFilesRef.current = null;
  }, []);

  useEffect(() => {
    if (selectedUploadTypeRef.current === selectedUploadType) return;
    selectedUploadTypeRef.current = selectedUploadType;
    if (filesRef.current.length === 0) return;

    revokeFilePreviews(filesRef.current);
    zipGenerationVersionRef.current += 1;
    pendingImageFilesRef.current = null;
    filesRef.current = [];
    setFiles([]);
    setPreviewPage(0);
    onChange(undefined);
    onUpload(null);
  }, [selectedUploadType]);

  const createFileWrapper = (file, idx, relativePath) => ({
    id: `${relativePath || file.webkitRelativePath || file.name}-${nextFileIdRef.current++}`,
    file,
    relativePath: relativePath || file.webkitRelativePath || file.name,
    preview: null,
  });

  const handleFiles = async (newFiles) => {
    setError(null);

    const selectedFiles = newFiles.filter((fileWrapper) => fileWrapper?.file);

    const isZipFile = selectedFiles.every((fileWrapper) => fileWrapper.file.name.endsWith('.zip'));
    const allowedMediaFiles = selectedFiles.filter((fileWrapper) =>
      isAllowedMediaFile(fileWrapper.file)
    );


    if (isZipFile && selectedFiles.length === 1) {

      setPreviewPage(0);
      replaceFiles(selectedFiles);
      handleZipUpload(selectedFiles[0].file);
    } else if (allowedMediaFiles.length > 0) {
      selectedFiles
        .filter((fileWrapper) => !allowedMediaFiles.includes(fileWrapper))
        .forEach((fileWrapper) => {
          if (fileWrapper.preview) URL.revokeObjectURL(fileWrapper.preview);
        });
      const canAppend = filesRef.current.every((fileWrapper) =>
        isAllowedMediaFile(fileWrapper.file)
      );
      const combinedFiles = canAppend
        ? [...filesRef.current, ...allowedMediaFiles]
        : allowedMediaFiles;
      const nextPreviewPage = canAppend ? previewPage : 0;

      if (!canAppend) revokeFilePreviews(filesRef.current);
      activateVisiblePreviews(
        combinedFiles,
        nextPreviewPage * MAX_FILE_PREVIEWS
      );
      setPreviewPage(nextPreviewPage);
      filesRef.current = combinedFiles;
      setFiles(combinedFiles);
      onChange(undefined);
      onUpload(null);
      handleImageUpload(combinedFiles);
      if (allowedMediaFiles.length !== selectedFiles.length) {
        setError(
          isVideoUpload
            ? "Only supported video files were added."
            : "Only supported image files were added."
        );
      }
    } else {

      revokeFilePreviews(selectedFiles);
      setError(
        isVideoUpload
          ? "Select a .zip file or supported video files."
          : "Select a .zip file or supported image files."
      );
    }
  };


  const handleZipUpload = (zipFile) => {
    zipGenerationVersionRef.current += 1;
    pendingImageFilesRef.current = null;
    onChange(zipFile.name);
    onUpload(zipFile);
  };

  const getISTTimestamp = () => {
    const now = new Date();

    const istTime = new Date(now.getTime());


    // Format IST date and time into a unique string
    const datePart = istTime.toISOString().split("T")[0]; // yyyy-mm-dd
    const timePart = istTime
      .toTimeString()
      .split(" ")[0]
      .replace(/:/g, "_"); // HH_mm_ss
    return `${datePart}_${timePart}`;
  }

  const handleImageUpload = async (imageFiles) => {
    const version = ++zipGenerationVersionRef.current;
    pendingImageFilesRef.current = { imageFiles, version };
    if (zipGenerationRunningRef.current) return;

    zipGenerationRunningRef.current = true;
    try {
      while (pendingImageFilesRef.current) {
        const pendingGeneration = pendingImageFilesRef.current;
        pendingImageFilesRef.current = null;
        const zip = new JSZip();
        const usedZipPaths = new Set();

        pendingGeneration.imageFiles.forEach((fileWrapper) => {
          const { file } = fileWrapper;
          const originalPath = (fileWrapper.relativePath || file.webkitRelativePath || file.name)
            .replace(/^\/+/, "");
          let zipPath = originalPath;
          let duplicateIndex = 2;

          while (usedZipPaths.has(zipPath)) {
            const extensionIndex = originalPath.lastIndexOf(".");
            const name = extensionIndex > 0 ? originalPath.slice(0, extensionIndex) : originalPath;
            const extension = extensionIndex > 0 ? originalPath.slice(extensionIndex) : "";
            zipPath = `${name} (${duplicateIndex})${extension}`;
            duplicateIndex += 1;
          }

          usedZipPaths.add(zipPath);
          zip.file(zipPath, file);
        });

        const zipContent = await zip.generateAsync({
          type: "blob",
          compression: "STORE",
          streamFiles: true,
        });

        if (pendingGeneration.version !== zipGenerationVersionRef.current) continue;

        const timestamp = getISTTimestamp();
        const zipFileName = `uploaded_media_${timestamp}.zip`;
        const zipFile = new File([zipContent], zipFileName, { type: "application/zip" });

        onChange(zipFile.name);
        onUpload(zipFile);
      }
    } catch (err) {
      console.error("Error compressing images:", err);
      setError("Failed to compress images. Please try again.");
    } finally {
      zipGenerationRunningRef.current = false;
    }
  };

  const removeFile = (id) => {
    const removedFile = filesRef.current.find((fileWrapper) => fileWrapper.id === id);
    if (removedFile?.preview) URL.revokeObjectURL(removedFile.preview);
    const nextFiles = filesRef.current.filter((fileWrapper) => fileWrapper.id !== id);
    const lastPreviewPage = Math.max(0, Math.ceil(nextFiles.length / MAX_FILE_PREVIEWS) - 1);
    const nextPreviewPage = Math.min(previewPage, lastPreviewPage);
    activateVisiblePreviews(nextFiles, nextPreviewPage * MAX_FILE_PREVIEWS);
    setPreviewPage(nextPreviewPage);
    filesRef.current = nextFiles;
    setFiles(nextFiles);
    if (nextFiles.length > 0) {
      onChange(undefined);
      onUpload(null);
      handleImageUpload(nextFiles);
    } else {
      zipGenerationVersionRef.current += 1;
      pendingImageFilesRef.current = null;
      onChange(undefined);
      onUpload(null);
    }
  };


  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const readDirectoryEntries = (directoryReader) =>
    new Promise((resolve, reject) => {
      const entries = [];

      const readEntries = () => {
        directoryReader.readEntries((batch) => {
          if (!batch.length) {
            resolve(entries);
            return;
          }

          entries.push(...batch);
          readEntries();
        }, reject);
      };

      readEntries();
    });

  const readEntryFiles = (entry, path = "") =>
    new Promise((resolve) => {
      if (entry.isFile) {
        entry.file(
          (file) => resolve([{ file, relativePath: `${path}${file.name}` }]),
          () => resolve([])
        );
        return;
      }

      if (entry.isDirectory) {
        readDirectoryEntries(entry.createReader())
          .then(async (entries) => {
            const nestedFiles = await Promise.all(
              entries.map((childEntry) => readEntryFiles(childEntry, `${path}${entry.name}/`))
            );
            resolve(nestedFiles.flat());
          })
          .catch(() => resolve([]));
        return;
      }

      resolve([]);
    });

  const getDroppedFileWrappers = async (dataTransfer) => {
    const items = Array.from(dataTransfer.items || []);
    const entries = items
      .map((item) => item.webkitGetAsEntry?.())
      .filter(Boolean);

    if (entries.length) {
      const droppedFiles = await Promise.all(entries.map((entry) => readEntryFiles(entry)));
      return droppedFiles
        .flat()
        .map(({ file, relativePath }, idx) => createFileWrapper(file, idx, relativePath));
    }

    return Array.from(dataTransfer.files).map((file, idx) => createFileWrapper(file, idx));
  };


  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFiles = await getDroppedFileWrappers(e.dataTransfer);

    handleFiles(droppedFiles);
  };

  useEffect(() => {
    const seedFiles = formContext?.initialFiles;
    if (!Array.isArray(seedFiles) || !seedFiles.length || initialFilesAppliedRef.current) {
      return undefined;
    }

    initialFilesAppliedRef.current = true;
    const wrappers = seedFiles.map((file, idx) => createFileWrapper(file, idx));
    handleFiles(wrappers);
    formContext?.clearInitialFiles?.();

    return undefined;
    // Apply once when the upload widget mounts with seed files from the empty-state dropzone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formContext?.initialFiles]);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFolderSelect = () => {
    folderInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files).map((file, idx) => createFileWrapper(file, idx));

    handleFiles(selectedFiles);
    e.target.value = "";
  };

  const handleFolderChange = (e) => {
    const selectedFiles = Array.from(e.target.files).map((file, idx) =>
      createFileWrapper(file, idx, file.webkitRelativePath)
    );

    handleFiles(selectedFiles);
    e.target.value = "";
  };

  const clearAllFiles = (e) => {
    e.preventDefault();
    e.stopPropagation();

    revokeFilePreviews(filesRef.current);
    zipGenerationVersionRef.current += 1;
    pendingImageFilesRef.current = null;
    setPreviewPage(0);
    filesRef.current = [];
    setFiles([]);
    onChange(undefined);
    onUpload(null);
  };

  const previewStartIndex = previewPage * MAX_FILE_PREVIEWS;
  const visibleFiles = files.slice(
    previewStartIndex,
    previewStartIndex + MAX_FILE_PREVIEWS
  );
  const hasPreviousPreviews = previewPage > 0;
  const hasMorePreviews =
    previewStartIndex + MAX_FILE_PREVIEWS < files.length;

  const showPreviewPage = (nextPage) => {
    activateVisiblePreviews(
      filesRef.current,
      nextPage * MAX_FILE_PREVIEWS
    );
    setPreviewPage(nextPage);
    window.requestAnimationFrame(() => {
      if (previewContainerRef.current) {
        previewContainerRef.current.scrollTop = 0;
      }
    });
  };




  return (
    <div className="file-upload-container">
      {files.length > 0 && (
        <div
          className="mb-2 d-flex justify-content-between align-items-center gap-2"
        >
          {/* Uploading indicator */}
          {uploading && (
            <div className="d-flex align-items-center gap-1 text-secondary">
              <span style={{ fontSize: "12px" }}>
                Uploading
              </span>
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          )}

          {/* Clear All */}
          <button
            type="button"
            className="plain-button2 text-danger fw-bold"
            onClick={clearAllFiles}
            disabled={uploading}
          >
            Clear All
          </button>
        </div>
      )}
      <div className="w-100 h-100">
        <div
          className={`em-dropzone text-center d-a flex-column ${files.length === 0 ? "h-100" : ""
            }`}
          style={{
            border: dragOver
              ? "2px dashed var(--leading-color)"
              : "2px dashed var(--glass-color)",
            cursor: "pointer",
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleFileSelect}
        >
          {files.length === 0 ? (
            <>
              <UploadIcon className="em-drop-icon mb-2 wf-icon" />
              <p className="bold-text mb-0">Drag & Drop files</p>
              <p className="em-drop-subtext">or browse your computer</p>
              <p className="small-text-secondary mb-0">
                {isVideoUpload
                  ? "Supported formats: .zip, .mp4, .webm, .mov, .avi, .mkv, .m4v, .mpeg, .mpg"
                  : "Supported formats: .zip, .jpg, .jpeg, .png, .webp, .tif, .tiff, .bmp"}
              </p>

               <span className="my-2 text-secondary">-------------- or -------------</span>


              <button
                type="button"
                className="plain-button2 text-primary container-card py-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFolderSelect();
                }}
              >
                Select folder
              </button>
            </>
          ) : (
            <div
              ref={previewContainerRef}
              className="w-100 d-a flex-wrap pt-2"
              style={{
                height: "280px",
                overflowY: "auto",
                alignContent: "flex-start",
              }}
            >
              {hasPreviousPreviews && (
                <button
                  type="button"
                  className="plain-button2 text-primary w-100 mb-2"
                  aria-label="View previous 50 files"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    showPreviewPage(previewPage - 1);
                  }}
                >
                  <FontAwesomeIcon icon={faChevronUp} className="me-1" />
                  loadmore
                </button>
              )}

              {visibleFiles.map((f, idx) => (
                <div
                  key={f.id}
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginRight: "8px",
                  }}
                >
                  {f.preview && isVideoFile(f.file) ? (
                    <video
                      src={f.preview}
                      muted
                      playsInline
                      preload="metadata"
                      aria-label={`video-preview-${previewStartIndex + idx}`}
                      style={{
                        width: "60px",
                        height: "50px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: "1px solid var(--glass-color)",
                      }}
                    />
                  ) : f.preview ? (
                    <img
                      src={f.preview}
                      alt={`preview-${previewStartIndex + idx}`}
                      style={{
                        maxWidth: "60px",
                        maxHeight: "50px",
                        borderRadius: "8px",
                        border: "1px solid var(--glass-color)",
                      }}
                    />
                  ) : f.file.name.toLowerCase().endsWith(".zip") ? (
                    <div
                      className="d-flex align-items-center gap-2 px-2 py-1"
                      style={{
                        maxWidth: "220px",
                        border: "1px solid var(--glass-color)",
                        borderRadius: "8px",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faFileZipper}
                        className="text-primary flex-shrink-0"
                        style={{ fontSize: "24px" }}
                      />
                      <span
                        className="text-truncate"
                        title={f.file.name}
                        style={{ fontSize: "12px" }}
                      >
                        {f.file.name}
                      </span>
                    </div>
                  ) : (
                    <span style={{ fontSize: "12px" }}>{f.file.name}</span>
                  )}

                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeFile(f.id);
                    }}
                    style={{
                      position: "absolute",
                      top: "-5px",
                      right: "-5px",
                      cursor: "pointer",
                      borderRadius: "50%",
                      background: "#fff",
                    }}
                  />
                </div>
              ))}
              {files.length > MAX_FILE_PREVIEWS && (
                <div className="small-text-secondary w-100 mt-2">
                  Showing {previewStartIndex + 1}-
                  {Math.min(previewStartIndex + visibleFiles.length, files.length)} of{" "}
                  {files.length} selected files.
                </div>
              )}

              {hasMorePreviews && (
                <button
                  type="button"
                  className="plain-button2 text-primary w-100 mt-2"
                  aria-label="View next 50 files"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    showPreviewPage(previewPage + 1);
                  }}
                >
                  <FontAwesomeIcon icon={faChevronDown} className="me-1" />
                  loadmore
                </button>
              )}

            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedFileTypes}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          webkitdirectory=""
          directory=""
          style={{ display: "none" }}
          onChange={handleFolderChange}
        />
      </div>

    </div>
  );
};

const VideoWidget = forwardRef(({ filehandler, streamingUrl }, ref) => {
  const videoRecorderRef = useRef(null);

  useImperativeHandle(ref, () => ({
    createZip: async () => {
      if (videoRecorderRef.current) {
        return await videoRecorderRef.current.createZip();
      }
      return null;
    }
  }));

  return (
    <div>
      <VideoRecorder
        ref={videoRecorderRef}
        handlefile={filehandler}
        streamurl={streamingUrl}
      />
    </div>
  );
});
var activeStep = 0;
export default function AddData({ datasetId, close, reload, initialFiles = null, initialDataSource = null }) {
  const setAlert = useContext(alertContext);
  const [schema, setSchema] = useState();
  const [uischema, setUischema] = useState();
  const [formData, setFormData] = useState(() => (
    initialDataSource ? { "Select Data Source": initialDataSource } : {}
  ));
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  // const [activeStep, setActiveStep] = useState(0);
  const [tempCredentials, setTempCredentials] = useState();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState(null);
  const [streamingUrl, setStreamingUrl] = useState();
  const [loadingAction, setLoadingAction] = useState(null);
  const [seedFiles, setSeedFiles] = useState(() =>
    Array.isArray(initialFiles) && initialFiles.length ? initialFiles : null
  );
  const videoRecorderRef = useRef(null);
  const aiAssistDefaultedRef = useRef(false);

  const getFoundationModelOption = useCallback((schemaObj) => {
    const aiAssistOptions =
      schemaObj?.dependencies?.["Use AI to Annotate"]?.oneOf?.[0]
        ?.properties?.["AI Assist"]?.enum || [];

    return aiAssistOptions.find((option) =>
      String(option).toLowerCase().includes("foundation")
    );
  }, []);

  const buildCustomUiSchema = useCallback((baseUiSchema = {}) => ({
    ...baseUiSchema,
    "Select Data Source": {
      ...(baseUiSchema?.["Select Data Source"] || {}),
      "ui:widget": "DataSourceCardWidget",
      "ui:options": { label: false, description: false },
    },
    "Upload Data Type": {
      ...(baseUiSchema?.["Upload Data Type"] || {}),
      "ui:options": {
        ...(baseUiSchema?.["Upload Data Type"]?.["ui:options"] || {}),
        label: false,
      },
    },
    Content: {
      ...(baseUiSchema?.Content || {}),
      "ui:widget": "ContentCardWidget",
      "ui:options": { label: false, description: false },
    },
    "Avoid Similar Images": {
      ...(baseUiSchema?.["Avoid Similar Images"] || {}),
      "ui:field": "ToggleCardField",
      "ui:options": { label: false, description: false }
    },
    "Use AI to Annotate": {
      ...(baseUiSchema?.["Use AI to Annotate"] || {}),
      "ui:field": "ToggleCardField",
      "ui:options": { label: false, description: false }
    },
    "Video for Inference": {
      ...(baseUiSchema?.["Video for Inference"] || {}),
      "ui:field": "ToggleCardField",
      "ui:options": { label: false, description: false }
    },
    "Bucket Type": {
      ...(baseUiSchema?.["Bucket Type"] || {}),
      "ui:widget": "radio",
      "ui:inline": true,
      "ui:options": { label: false }
    },
    "Directory Format": {
      ...(baseUiSchema?.["Directory Format"] || {}),
      "ui:widget": "radio",
      "ui:inline": true,
      "ui:options": { label: false }
    },
    "Annotation Format": {
      ...(baseUiSchema?.["Annotation Format"] || {}),
      "ui:widget": "radio",
      "ui:inline": true,
      "ui:options": { label: false }
    },
    "Similarity Threshold": { "ui:widget": "hidden" },
    "Task Name": { "ui:widget": "hidden" },
    "Taskset Group Name": { "ui:widget": "hidden" },
    "Images Per Task": { "ui:widget": "hidden" },
    "Tasks Per Taskset": { "ui:widget": "hidden" },
    Labels: {
      "ui:field": "LabelsPromptArrayField",
      "ui:options": { label: false, description: false },
    },
    labels: {
      "ui:field": "LabelsPromptArrayField",
      "ui:options": { label: false, description: false },
    },
    "AI Assist": { "ui:field": () => null },
    "Model Details": { "ui:field": () => null },
    "Experiment Details": { "ui:field": () => null },
    "Foundational Models": { "ui:field": () => null },
    "Foundational Model Details": { "ui:field": () => null },
  }), []);


  const DataSourceCardWidget = ({ value, onChange }) => {
    const options = [
      { value: "BrowserUpload", icon: <UploadIcon className='wf-icon' />, label: "Local PC ", },
      { value: "CloudS3", icon: <AddDatasetIcon className='wf-icon' />, label: "Cloud Transfer (S3)", },
      // { value: "LocalTransfer", label: "Network Transfer", icon: <NetworkIcon className='wf-icon' /> },
      // { value: "Registered_Device", label: "Registered Device", icon: <PlatformIcon className='wf-icon' /> },
      { value: "PlatformImport", label: "Platform Import", icon: <PlatformIcon className='wf-icon' /> },
    ];

    return (
      <div className="row g-2">
        {options.map((opt, i) => (
          <div className="col-6 col-sm-4" key={i}>
            <div
              key={opt.value}
              onClick={() => onChange(opt.value)}
              className={`em-source-tile d-flex justify-content-between gap-2 ${value === opt.value ? "active cpf-hw-item--active" : ""}`}
            >
              <div className="d-flex flex-column align-items-start">
                {opt.icon}
                <span className="bold-text  mt-1 mb-0">{opt.label}</span>
              </div>
              <div class="cpf-hw-dot"></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ContentCardWidget = ({ value, onChange, schema }) => {
    const enums = schema.enum || [];
    const enumNames = schema.enumNames || enums;

    const options = enums.map((val, idx) => {
      const label = enumNames[idx] || val;
      const match = label.match(/^(.+?)(?:\s*\((.+)\))?$/);

      return {
        value: val,
        title: match?.[1] ?? label,
        description: match?.[2] ?? "",
      };
    });

    return (
      <div className="row g-3">
        {options.map((opt) => {
          const selected = value === opt.value;

          return (
            <div className="col-12 col-md-4" key={opt.value}>
              <div
                role="button"
                onClick={() => onChange(opt.value)}
                className={`content-card ${selected ? "active" : ""}`}
              >
                <div className="content-card__radio">
                  <input type="radio" checked={selected} readOnly />
                </div>

                <div className="bold-text">
                  {opt.title}
                </div>

                {opt.description && (
                  <small className="small-text-secondary">
                    {opt.description}
                  </small>
                )}

              </div>
            </div>
          );
        })}
      </div>
    );
  };



  const ToggleCardField = (props) => {
    const {
      idSchema,
      title,
      formData,
      onChange,
      schema,
      uiSchema,
      formContext,
    } = props;

    const value = !!formData;

    const rawName = idSchema.$id
      ?.split("_")
      .pop()
      ?.replace(/_/g, " ");

    const fieldTitle =
      uiSchema?.["ui:title"] ||
      title ||
      schema?.title ||
      rawName;

    const fieldDescription =
      uiSchema?.["ui:description"] || schema?.description;

    const isAvoidSimilarImages = rawName === "Avoid Similar Images";
    const isUseAIAnnotate = rawName === "Use AI to Annotate";

    const fullSchema = formContext?.schema;

    /* ---------- helpers ---------- */
    const getFieldLabel = (fieldSchema, fallback) =>
      (fieldSchema?.title || fallback || "").replace(/_/g, " ");

    const isRequiredField = (parentSchema, fieldName) =>
      parentSchema?.required?.includes(fieldName);

    const getMatchingDependencyProperties = (parentSchema, fieldKey, fieldValue) => {
      const dependency = parentSchema?.dependencies?.[fieldKey];

      if (!Array.isArray(dependency?.oneOf)) return [];

      const selectedBranch = dependency.oneOf.find(
        (branch) => branch?.properties?.[fieldKey]?.const === fieldValue
      );

      return Object.entries(selectedBranch?.properties || {}).filter(
        ([dependentKey]) => dependentKey !== fieldKey
      );
    };

    const updateNestedSection = (sectionKey, fieldKey, nextValue) => {
      formContext.setFormData((prev) => ({
        ...prev,
        [sectionKey]: {
          ...(prev[sectionKey] || {}),
          [fieldKey]: nextValue,
        },
      }));
    };

    const getNestedFieldValue = (sectionKey, fieldKey, fieldSchema) => {
      const value = formContext?.formData?.[sectionKey]?.[fieldKey];

      if (value !== undefined && value !== null) return value;
      if (fieldSchema?.default !== undefined && fieldSchema?.default !== null) {
        return fieldSchema.default;
      }

      return "";
    };

    const renderSectionField = (sectionKey, sectionSchema, fieldKey, fieldSchema) => {
      const value = getNestedFieldValue(sectionKey, fieldKey, fieldSchema);
      const label = getFieldLabel(fieldSchema, fieldKey);
      const required = isRequiredField(sectionSchema, fieldKey);
      const missingRequiredValue =
        required &&
        (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0)
        );
      const fieldError =
        formContext?.hasTriedSubmit && missingRequiredValue
          ? `${label} is required.`
          : "";
      const fieldErrorMessage = fieldError ? (
        <div className="text-danger small fw-semibold mt-1">
          {fieldError}
        </div>
      ) : null;

      if (fieldSchema?.enum) {
        return (
          <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
            <label className="small d-flex mb-1">
              {label}
              {required && <span className="text-danger ms-1">*</span>}
            </label>

            <select
              className={`form-select ${fieldError ? "is-invalid" : ""}`}
              value={value || ""}
              onChange={(e) => updateNestedSection(sectionKey, fieldKey, e.target.value)}
            >
              <option value="">Select</option>
              {(fieldSchema.enum || []).map((optionValue, optionIndex) => (
                <option key={optionValue} value={optionValue}>
                  {fieldSchema.enumNames?.[optionIndex] || optionValue}
                </option>
              ))}
            </select>

            {fieldErrorMessage}

            {fieldSchema?.description && (
              <div className="text-secondary small mt-1">{fieldSchema.description}</div>
            )}
          </div>
        );
      }

      if (fieldSchema?.type === "number" || fieldSchema?.type === "integer") {
        return (
          <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
            <label className="small d-flex mb-1">
              {label}
              {required && <span className="text-danger ms-1">*</span>}
            </label>

            <input
              type="number"
              min={fieldSchema?.minimum}
              max={fieldSchema?.maximum}
              step={fieldSchema?.multipleOf || 0.01}
              className={`form-control ${fieldError ? "is-invalid" : ""}`}
              value={value ?? ""}
              onChange={(e) =>
                updateNestedSection(
                  sectionKey,
                  fieldKey,
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
            />

            {fieldErrorMessage}

            {fieldSchema?.description && (
              <div className="text-secondary small mt-1">{fieldSchema.description}</div>
            )}
          </div>
        );
      }

      if (isLabelsPromptField(fieldKey, fieldSchema)) {
        const hasLabels = String(value || "").trim().length > 0;

        return (
          <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
            <div className="dataset-label-field-header">
              <label className="form-label mb-0">
                {label}
                {required && <span className="text-danger ms-1">*</span>}
              </label>

              {hasLabels && (
                <button
                  type="button"
                  className="dataset-label-clear-button"
                  onClick={() => updateNestedSection(sectionKey, fieldKey, "")}
                >
                  Clear all
                </button>
              )}
            </div>

            <LabelChipsInput
              value={value}
              invalid={Boolean(fieldError)}
              onChange={(nextLabels) =>
                updateNestedSection(sectionKey, fieldKey, nextLabels)
              }
              ariaLabel={label}
            />

            {fieldErrorMessage}

            {fieldSchema?.description && (
              <div className="form-help mt-1">{fieldSchema.description}</div>
            )}
          </div>
        );
      }

      if (isNamePromptLabelArray(fieldSchema)) {
        return (
          <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
            <LabelsPromptEditor
              title={label}
              description={fieldSchema?.description || ""}
              value={Array.isArray(value) ? value : []}
              onChange={(nextLabels) =>
                updateNestedSection(sectionKey, fieldKey, nextLabels)
              }
              itemSchema={fieldSchema?.items || {}}
              required={required}
              showErrors={Boolean(formContext?.hasTriedSubmit)}
              errorMessage={fieldError}
            />
          </div>
        );
      }

      return (
        <div className="mb-2" key={`${sectionKey}-${fieldKey}`}>
          <label className="form-label mb-1">
            {label}
            {required && <span className="text-danger ms-1">*</span>}
          </label>

          <input
            type="text"
            className={`form-control ${fieldError ? "is-invalid" : ""}`}
            value={value}
            onChange={(e) => updateNestedSection(sectionKey, fieldKey, e.target.value)}
          />

          {fieldErrorMessage}

          {fieldSchema?.description && (
            <div className="form-help mt-1">{fieldSchema.description}</div>
          )}
        </div>
      );
    };

    const renderSectionFieldWithDependencies = (
      sectionKey,
      sectionSchema,
      fieldKey,
      fieldSchema
    ) => {
      const value = getNestedFieldValue(sectionKey, fieldKey, fieldSchema);
      const dependentFields = getMatchingDependencyProperties(
        sectionSchema,
        fieldKey,
        value
      );

      return (
        <div key={`${sectionKey}-${fieldKey}`}>
          {renderSectionField(sectionKey, sectionSchema, fieldKey, fieldSchema)}

          {dependentFields.map(([dependentKey, dependentSchema]) => (
            <div className="ms-2" key={`${sectionKey}-${fieldKey}-${dependentKey}`}>
              {renderSectionFieldWithDependencies(
                sectionKey,
                sectionSchema,
                dependentKey,
                dependentSchema
              )}
            </div>
          ))}
        </div>
      );
    };

    const renderSelectedAIAssistSections = () => {
      const selectedAIAssist = formContext?.formData?.["AI Assist"];

      if (!selectedAIAssist) return null;

      const selectedDependency = fullSchema?.dependencies?.["AI Assist"]?.oneOf?.find(
        (dependency) => dependency?.properties?.["AI Assist"]?.const === selectedAIAssist
      );

      const visibleSections = Object.entries(selectedDependency?.properties || {}).filter(
        ([key, value]) => key !== "AI Assist" && value?.type === "object"
      );

      if (visibleSections.length === 0) return null;

      return visibleSections.map(([sectionKey, sectionSchema]) => (
        <div className="em-toggle-card__sub-block" key={sectionKey}>
          <label className="form-label mb-2">
            {getFieldLabel(sectionSchema, sectionKey)}
          </label>

          {Object.entries(sectionSchema?.properties || {}).map(([fieldKey, fieldSchema]) =>
            renderSectionFieldWithDependencies(
              sectionKey,
              sectionSchema,
              fieldKey,
              fieldSchema
            )
          )}
        </div>
      ));
    };

    /* ---------- Similarity ---------- */
    const similarityValue =
      formContext?.formData?.["Similarity Threshold"] ?? 0.95;

    const handleSliderChange = (e) => {
      const percent = Number(e.target.value);
      const normalized = percent / 100;

      formContext.setFormData((prev) => ({
        ...prev,
        "Similarity Threshold": normalized,
      }));
    };

    return (
      <div className="em-toggle-card">
        <div className="em-toggle-card__left">
          <div className="bold-text">{fieldTitle}</div>

          {fieldDescription && (
            <div className="small-text-secondary">
              {fieldDescription}
            </div>
          )}

          {/* ---------- Avoid Similar Images ---------- */}
          {isAvoidSimilarImages && value && (
            <div className="em-toggle-card__slider-block">
              <div className="em-toggle-card__slider-header">
                <span>Similarity Cap</span>
                <strong>{Math.round(similarityValue * 100)}%</strong>
              </div>

              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={similarityValue * 100}
                onChange={handleSliderChange}
                className="em-toggle-card__range"
              />

              <div className="em-toggle-card__hint">
                Excludes assets with a perceptual hash match higher than this value.
              </div>
            </div>
          )}

          {/* ---------- AI Annotate ---------- */}
          {isUseAIAnnotate && value && (
            <div className="em-toggle-card__ai-block">
              {/* Task Name */}
              {(() => {
                const aiAnnotateSchema =
                  fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf?.find(
                    (dependency) =>
                      dependency?.properties?.["Use AI to Annotate"]?.const === true
                  ) || fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf?.[0];
                const taskSchema = aiAnnotateSchema?.properties?.["Task Name"];

                return (
                  <div className="mb-3">
                    <label className="form-label mb-1">
                      {getFieldLabel(taskSchema, "Task Name")}
                      {isRequiredField(
                        aiAnnotateSchema,
                        "Task Name"
                      ) && <span className="text-danger ms-1">*</span>}
                    </label>

                    <input
                      className="form-control"
                      value={formContext?.formData?.["Task Name"] || ""}
                      onChange={(e) =>
                        formContext.setFormData((prev) => ({
                          ...prev,
                          "Task Name": e.target.value,
                        }))
                      }
                    />
                  </div>
                );
              })()}

              {/* AI Assist */}
              <div className="mb-3">
                <label className="form-label mb-1">
                  AI Assist
                </label>

                <div className="d-flex gap-2 flex-wrap container-card" style={{ background: 'var(--card-color)' }}>
                  {(
                    fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf?.[0]
                      ?.properties?.["AI Assist"]?.enum || []
                  ).map((opt, i) => {
                    const isSelected = formContext?.formData?.["AI Assist"] === opt;

                    const label =
                      fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf?.[0]
                        ?.properties?.["AI Assist"]?.enumNames?.[i] || opt;

                    return (
                      <div
                        key={opt}
                        role="button"
                        onClick={() =>
                          formContext.setFormData((prev) =>
                            sanitizeDatasetWizardData({
                              ...prev,
                              "AI Assist": opt,
                            })
                          )
                        }
                        className="d-flex align-items-center gap-2 cursor-pointer container-card rounded px-3 p-1"
                        style={{ borderColor: isSelected && "var(--leading-color)" }}
                      >
                        <CheckIcon
                          className={`wf-icon-small ${isSelected ? "text-primary" : "text-secondary"
                            }`}
                        />

                        <span className={isSelected ? "small-font text-primary fw-bold" : "small-font text-secondary"}>
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {renderSelectedAIAssistSections()}

              {/* Root AI sizing fields — once here (hidden from RJSF to avoid duplicates) */}
              {(() => {
                const aiAnnotateSchema =
                  fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf?.find(
                    (dependency) =>
                      dependency?.properties?.["Use AI to Annotate"]?.const === true
                  ) || fullSchema?.dependencies?.["Use AI to Annotate"]?.oneOf?.[0];

                const sizingFields = [
                  "Taskset Group Name",
                  "Images Per Task",
                  "Tasks Per Taskset",
                ];

                return sizingFields.map((fieldKey) => {
                  const fieldSchema = aiAnnotateSchema?.properties?.[fieldKey];
                  if (!fieldSchema || fieldSchema.type === "object") return null;

                  const label = getFieldLabel(fieldSchema, fieldKey);
                  const required = isRequiredField(aiAnnotateSchema, fieldKey);
                  const fieldValue = formContext?.formData?.[fieldKey] ?? "";

                  return (
                    <div className="mb-3" key={fieldKey}>
                      <label className="form-label mb-1">
                        {label}
                        {required && <span className="text-danger ms-1">*</span>}
                      </label>
                      <input
                        type={
                          fieldSchema.type === "number" || fieldSchema.type === "integer"
                            ? "number"
                            : "text"
                        }
                        className="form-control"
                        value={fieldValue}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const nextValue =
                            fieldSchema.type === "number" || fieldSchema.type === "integer"
                              ? raw === ""
                                ? undefined
                                : Number(raw)
                              : raw;
                          formContext.setFormData((prev) => ({
                            ...prev,
                            [fieldKey]: nextValue,
                          }));
                        }}
                      />
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        <label className="em-toggle-card__switch">
          <input
            type="checkbox"
            checked={value}
            onChange={() => onChange(!value)}
          />
          <span className="em-toggle-card__slider" />
        </label>
      </div>
    );
  };



  const widgets = useMemo(() => ({
    ...CustomWidget,
    ...unlabeledRjsfWidgets,
    DataSourceCardWidget,
    ContentCardWidget,   // 👈 add this
    FileWidget: (props) => (
      <FileWidget
        {...props}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onUpload={handleFileSelect}
      />
    ),
    VideoWidget: (props) => (
      <VideoWidget
        {...props}
        ref={videoRecorderRef}
        filehandler={filehandler}
        streamingUrl={streamingUrl}
      />
    ),
    RangeWidget: defaultRangeWidget
  }), [streamingUrl, uploading, uploadProgress]);

  const fields = useMemo(
    () => ({ ToggleCardField, LabelsPromptArrayField }),
    []
  );

  const CustomObjectFieldTemplate = useCallback((props) => {
    const {
      title,
      description,
      properties,
      required,
      uiSchema,
      idSchema,
    } = props;

    if (uiSchema?.["ui:title"] === false) {
      return <>{properties.map((p) => p.content)}</>;
    }

    const isRootObject = idSchema?.$id === "root";

    return (
      <div className={`rjsf-object-field ${isRootObject ? "dataset-flow-fields" : ""}`}>
        {!isRootObject && title && title !== "root" && (
          <label className="form-label mt-0">
            {title}
            {required && <span className="text-danger ms-1">*</span>}
          </label>
        )}
        {!isRootObject && description}
        {properties.map((element) => (
          <div key={element.name} className="dataset-flow-form-field mb-0">
            {element.content}
          </div>
        ))}
      </div>
    );
  }, []);

  const InlineErrorFieldTemplate = useCallback((props) => {
    const {
      children,
      rawErrors = [],
      hidden,
      id,
      label,
      required,
      displayLabel,
      description,
      help,
      uiSchema,
      formContext,
      name,
      schema: fieldSchema,
    } = props;
    const shouldShowErrors = Boolean(formContext?.hasTriedSubmit);

    const hiddenIds = new Set([
      "root_AI Assist",
      "root_Model Details",
      "root_Experiment Details",
      "root_Foundational Models",
      "root_Foundational Model Details",
      "root_Task Name",
      "root_Taskset Group Name",
      "root_Images Per Task",
      "root_Tasks Per Taskset",
    ]);

    if (hidden || hiddenIds.has(id)) {
      return null;
    }

    const radioSectionIds = new Set([
      "root_Bucket Type",
      "root_Directory Format",
      "root_Annotation Format",
    ]);

    const hideLabel =
      uiSchema?.["ui:options"]?.label === false ||
      name === "Content" ||
      name === "Labels" ||
      name === "labels" ||
      fieldSchema?.["ui:widget"] === "ContentCardWidget";

    const hideDescription =
      uiSchema?.["ui:options"]?.description === false ||
      name === "Labels" ||
      name === "labels";

    const fieldContent = (
      <>
        {displayLabel && !hideLabel && label && (
          <label className="form-label">
            {label === "Upload Data" ? "" : label}
            {required && <span className="text-danger ms-1">*</span>}
          </label>
        )}
        {!hideDescription && description && (
          <div className="form-help">{description}</div>
        )}
        {shouldShowErrors && rawErrors.length > 0 && (
          <div className="text-danger small fw-semibold mb-1">
            {formatInlineValidationError(rawErrors[0])}
          </div>
        )}
        {children}
        {help}
      </>
    );

    const normalizedFieldId = String(id || "").replace(/_/g, " ");

    if (radioSectionIds.has(normalizedFieldId)) {
      return <div className="dataset-flow-radio-section">{fieldContent}</div>;
    }

    return fieldContent;
  }, []);

  const validateRequiredStrings = useCallback((schemaNode, dataNode, errorsNode) => {
    if (!schemaNode || !errorsNode) return;

    if (schemaNode.properties) {
      const requiredFields = schemaNode.required || [];

      requiredFields.forEach((fieldName) => {
        const fieldSchema = schemaNode.properties?.[fieldName];
        const fieldValue = dataNode?.[fieldName];

        if (!fieldSchema || !errorsNode[fieldName]) return;

        if (fieldSchema.type === "string") {
          if (
            fieldValue === undefined ||
            fieldValue === null ||
            typeof fieldValue !== "string" ||
            fieldValue.trim() === ""
          ) {
            errorsNode[fieldName].addError(`${fieldSchema.title || fieldName} is required.`);
          }
        }
      });

      Object.entries(schemaNode.properties).forEach(([fieldName, fieldSchema]) => {
        if (fieldSchema?.type === "object" && errorsNode[fieldName]) {
          validateRequiredStrings(fieldSchema, dataNode?.[fieldName] || {}, errorsNode[fieldName]);
        }
      });
    }

    if (schemaNode.dependencies) {
      Object.entries(schemaNode.dependencies).forEach(([fieldName, dependency]) => {
        if (!Array.isArray(dependency?.oneOf)) return;

        const selectedDependency = dependency.oneOf.find(
          (branch) => branch?.properties?.[fieldName]?.const === dataNode?.[fieldName]
        );

        if (selectedDependency) {
          validateRequiredStrings(selectedDependency, dataNode, errorsNode);
        }
      });
    }
  }, []);

  const customValidate = useCallback((currentFormData, errors) => {
    validateRequiredStrings(schema, currentFormData, errors);
    validateDuplicateLabelsInSchema(schema, currentFormData, errors);
    return errors;
  }, [schema, validateRequiredStrings]);

  const formContextValue = useMemo(
    () => ({
      formData,
      setFormData,
      schema,
      hasTriedSubmit,
      initialFiles: seedFiles,
      clearInitialFiles: () => setSeedFiles(null),
    }),
    [formData, schema, hasTriedSubmit, seedFiles]
  );

  const { data, loading, error, setLoad } = useFetch(
    `forms/data/add_data?dataset_id=${datasetId}`,
    "get"
  );

  useEffect(() => {
    if (!data) return;
    activeStep = 0;
    aiAssistDefaultedRef.current = false;
    // setActiveStep(0);
    setSchema(data.json_schema);
    setTempCredentials(data.temp_aws_cred);
    setStreamingUrl(data?.streaming?.["streaming-device"]?.[0]?.stream1?.webrtc_url);
    setUischema(buildCustomUiSchema(data.ui_schema));
    setFormData((prev) => ({
      ...prev,
      "Select Data Source":
        prev?.["Select Data Source"] ?? initialDataSource ?? "BrowserUpload",
    }));

    return () => {
      setCurrentFile(null);
      setUploadProgress(0);
      setUploading(false);
    };
  }, [data]);

  const handleFileSelect = (file) => {
    setCurrentFile(file || null);
    setUploadProgress(0);
    setUploading(false);
  };

  const handleFormChange = ({ formData: newFormData }) => {
    const nextFormData = sanitizeDatasetWizardData(newFormData);

    if (formData?.["Select Data Source"] !== newFormData?.["Select Data Source"]) {
      setHasTriedSubmit(false);
    }

    if (nextFormData?.["AI Assist"] && nextFormData?.["AI Assist"] !== "Use Model") {
      aiAssistDefaultedRef.current = true;
    }
    if (!isEnabledSelection(nextFormData?.["Use AI to Annotate"])) {
      aiAssistDefaultedRef.current = false;
    }
    setFormData(nextFormData);
  };

  const filehandler = (file) => {
    setCurrentFile(file);
  };

  useEffect(() => {
    if (!schema || aiAssistDefaultedRef.current) return;
    if (!formData?.["Use AI to Annotate"]) return;

    const foundationModelOption = getFoundationModelOption(schema);
    if (!foundationModelOption) return;

    if (!formData?.["AI Assist"] || formData?.["AI Assist"] === "Use Model") {
      aiAssistDefaultedRef.current = true;
      setFormData((prev) => ({
        ...prev,
        "AI Assist": foundationModelOption,
      }));
    }
  }, [formData, schema, getFoundationModelOption]);

  const handleS3Upload = async (file) => {
    if (!file || !tempCredentials) {
      console.error('Missing required upload parameters');
      throw new Error("Missing file or credentials");
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const uploadParams = {
        Bucket: S3_BUCKET,
        Key: file.name,
        Body: file
      };

      const uploader = new Upload({
        client: new S3Client({
          region: REGION,
          credentials: {
            accessKeyId: tempCredentials.aws_access_key_id,
            secretAccessKey: tempCredentials.aws_secret_access_key,
            sessionToken: tempCredentials.aws_session_token,
          },
        }),
        leavePartsOnError: false,
        params: uploadParams,
      });

      uploader.on("httpUploadProgress", (progress) => {
        const percentage = Math.round((progress.loaded / progress.total) * 100);
        setUploadProgress(percentage);
      });

      await uploader.done();

      return {
        access_key: tempCredentials.aws_access_key_id,
        secret_key: tempCredentials.aws_secret_access_key,
        session_token: tempCredentials.aws_session_token,
        bucket_name: S3_BUCKET,
        folder_name: file.name
      };
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleFormSubmit = async ({ formData: newFormData }, e) => {
    e.preventDefault();

    setLoad(true);
    setLoadingAction("next");

    try {
      let finalFormData = sanitizeDatasetWizardData({ ...formData, ...newFormData });
      const duplicateLabelsInSchema = getDuplicateLabelNamesInSchema(
        schema,
        finalFormData
      );

      if (duplicateLabelsInSchema.length > 0) {
        setAlert?.({
          message: formatDuplicateLabelsError(duplicateLabelsInSchema),
          status: "error",
        });
        setFormData(finalFormData);
        setHasTriedSubmit(true);
        setLoad(false);
        setLoadingAction(null);
        return;
      }

      if (activeStep === 0) {
        if (finalFormData["Select Data Source"] === "BrowserUpload") {
          if (!currentFile) {
            // setAlert("Please select a file first");
            setLoad(false);
            return;
          }

          try {
            const uploadResult = await handleS3Upload(currentFile);
            finalFormData = {
              ...finalFormData,
              Source: "BrowserUpload",
              ...uploadResult
            };
          } catch (error) {
            console.error('Browser upload failed:', error);
            // setAlert("Upload failed: " + error.message);
            setLoad(false);
            return;
          }
        }
        else if (finalFormData["Select Data Source"] === "Registered_Device") {
          try {
            const videoWidget = widgets.VideoWidget({});

            const zipFile = await videoRecorderRef.current?.createZip();

            if (!zipFile) {
              // setAlert("No recordings found. Please record or capture something first.");
              setLoad(false);
              return;
            }

            const uploadResult = await handleS3Upload(zipFile);

            finalFormData = {
              ...finalFormData,
              Source: "Registered_Device",
              ...uploadResult
            };
          } catch (error) {
            // console.error('Registered device processing failed:', error);
            // setAlert("Failed to process recordings: " + error.message);
            setLoad(false);
            return;
          }
        }
      }

      const response = await fetch(
        `${window.ENV.API_URL}/forms/data/add_data?dataset_id=${datasetId}&step=${activeStep}`,
        {
          method: "post",
          headers: {
            "Authorization": `Bearer ${getCokkie("jwtToken")}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(finalFormData)
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || "Submission failed");
      }

      if (result.summary) {
        const sanitizedSummary = sanitizeDatasetWizardData({
          ...finalFormData,
          ...result.summary,
        });
        setSchema({ configuration: sanitizedSummary });
        setFormData(
          mergeReviewDataWithEditableData(sanitizedSummary, finalFormData, schema)
        );
      } else {
        setSchema(result.json_schema);
        setUischema(buildCustomUiSchema(result.ui_schema));

        setFormData((prev) =>
          normalizeSchemaFormData(
            {
              ...prev,
              ...finalFormData,
              Content:
                finalFormData?.Content ??
                result.json_schema?.properties?.Content?.default ??
                "ImagesOnly"
            },
            result.json_schema
          )
        );
      }

      // setActiveStep(result.active_step || activeStep + 1);
      activeStep = result?.active_step ?? activeStep++;

    } catch (error) {
      console.error('Form submission failed:', error);
      // setAlert(error.message);
      setLoadingAction(null);
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };
  const handleBack = async (e) => {

    e.preventDefault();

    setLoad(true);
    setLoadingAction("back");

    try {
      // const prevStep = activeStep - 2;
      activeStep = activeStep - 2;

      const response = await fetch(
        `${window.ENV.API_URL}/forms/data/add_data?dataset_id=${datasetId}&step=${activeStep}`,
        {
          method: "post",
          headers: {
            "Authorization": `Bearer ${getCokkie("jwtToken")}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(formData)
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || "Navigation failed");
      }

      if (result.summary) {
        const transformedSummary = {
          ...result.summary,
          Source: formData["Select Data Source"] || formData.Source
        };
        const sanitizedSummary = sanitizeDatasetWizardData({
          ...formData,
          ...transformedSummary,
        });
        setFormData(
          mergeReviewDataWithEditableData(sanitizedSummary, formData, schema)
        );
        setSchema({ configuration: sanitizedSummary });
      } else {
        setSchema(result.json_schema);
        setUischema(buildCustomUiSchema(result.ui_schema));
        setFormData((prev) =>
          normalizeSchemaFormData(prev, result.json_schema)
        );
      }

      // setActiveStep(result.active_step || prevStep);
      activeStep = result?.active_step;

      if (activeStep === 0) {
        setCurrentFile(null);
        setUploadProgress(0);
        setUploading(false);
      }

    } catch (error) {
      console.error('Back navigation failed:', error);
      setLoadingAction(null);
      // setAlert(error.message);
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setLoadingAction('submit');
    setLoad(true);

    try {
      const finalData = sanitizeDatasetWizardData(schema.configuration);

      const response = await fetch(
        `${window.ENV.API_URL}/forms/data/add_data?dataset_id=${datasetId}&step=${activeStep}`,
        {
          method: "post",
          headers: {
            "Authorization": `Bearer ${getCokkie("jwtToken")}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(finalData)
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || "Final submission failed");
      }

      close?.("");
      reload?.();
    } catch (error) {
      console.error('Final submission failed:', error);
      setLoadingAction(null);
      // setAlert(error.message);
    } finally {
      setLoad(false);
      setLoadingAction(null);
    }
  };

  const isLastStep = schema && activeStep === data?.steppers?.length - 1;
  const steps = data?.steppers || [];
  const totalSteps = steps.length || 1;
  const currentStepLabel =
    steps?.[activeStep] || schema?.title || (isLastStep ? "Summary" : "Configuration");
  const currentStepNumber = Math.min(activeStep + 1, totalSteps);
  const visibleHeaderSteps = steps.length > 0 ? steps.slice(0, currentStepNumber) : [currentStepLabel];
  const getStepDescription = (stepLabel = "") => {
    const normalized = String(stepLabel || "").toLowerCase();

    if (isLastStep || normalized.includes("summary") || normalized.includes("review")) {
      return "Review the configuration carefully before submitting the data import.";
    }

    if (normalized.includes("data source") || normalized.includes("upload")) {
      return "Choose the source and prepare the data you want to bring into this dataset.";
    }

    if (normalized.includes("content")) {
      return "Select the content type and adjust the import settings for these files.";
    }

    if (normalized.includes("annotate") || normalized.includes("ai")) {
      return "Configure the automation and annotation settings for this import.";
    }

    return "Complete the required information for this step before continuing.";
  };

  return (
    <main className="open-form evaluate-model-ui h-100 d-flex flex-column dataset-flow-shell">
      <header className="d-flex align-items-start justify-content-between p-3 dataset-flow-shell__header">
        <div className="d-flex gap-2 align-items-start w-100">
          <DatasetIcon className="wf-icon-blue bg-primary text-white" />
          <div className="em-header flex-fill">
            <div className="d-flex justify-content-between w-100">
              <h2 className="main-title mb-0">Add More Data</h2>
              <button className="plain-button2 p-0" onClick={() => close("")}>
                <CloseIcon className="wf-icon text-secondary" />
              </button>
            </div>
            <p className="sub-title mb-0">
              Step {currentStepNumber} of {totalSteps}: {currentStepLabel}
            </p>
          </div>
        </div>
      </header>

      {/* BODY */}
      <section
        className="dataset-wizard__card flex-fill w-100 dataset-flow-shell__body"
      >


        {loading ? (
          <div className="dataset-flow-shell__loader">
            <Loader />
          </div>
          //   <div className="p-4">
          //   <Skeleton height={40} />
          //   <Skeleton height={200} className="mt-3" />
          // </div>
        ) : error ? (
          <ErrorScreen message={error} />
        ) : schema && !isLastStep ? (
          <>
            <div className="form-shell__intro">
              <div className="dataset-flow-shell__eyebrow">
                <div className="dataset-flow-shell__step-flow">
                  {visibleHeaderSteps.map((stepLabel, index) => {
                    const stepNumber = index + 1;
                    const isCurrentHeaderStep = stepNumber === currentStepNumber;

                    return (
                      <div
                        key={`${stepLabel}-${stepNumber}`}
                        className="dataset-flow-shell__step-item"
                      >
                        {isCurrentHeaderStep ? (
                          <span className="dataset-flow-shell__step-badge">
                            Step {stepNumber}
                          </span>
                        ) : (
                          <span className="dataset-flow-shell__step-marker">
                            {stepNumber}
                          </span>
                        )}
                        <span
                          className={`dataset-flow-shell__step-name ${isCurrentHeaderStep
                            ? "dataset-flow-shell__step-name--active"
                            : ""
                            }`}
                          style={{ color: '#b1c7e8' }}
                        >
                          {stepLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="mb-0 from-shell-sub-text">{getStepDescription(currentStepLabel)}</p>
            </div>

            <Form
              id="add-data-form"
              schema={{ ...schema, title: "" }}
              uiSchema={uischema}
              widgets={widgets}
              FieldTemplate={InlineErrorFieldTemplate}
              ObjectFieldTemplate={CustomObjectFieldTemplate}
              fields={fields}
              formContext={formContextValue}
              formData={formData}
              onChange={handleFormChange}
              onSubmit={handleFormSubmit}
              customValidate={customValidate}
              liveValidate={hasTriedSubmit}
              showErrorList={false}
              onError={() => setHasTriedSubmit(true)}
            >
              <button type="submit" className="d-none" />
            </Form>
          </>) : schema ? (
            <>
              <div className="form-shell__intro">
                <div className="dataset-flow-shell__eyebrow">
                  <div className="dataset-flow-shell__step-flow">
                    {visibleHeaderSteps.map((stepLabel, index) => {
                      const stepNumber = index + 1;
                      const isCurrentHeaderStep = stepNumber === currentStepNumber;

                      return (
                        <div
                          key={`${stepLabel}-${stepNumber}`}
                          className="dataset-flow-shell__step-item"
                        >
                          {isCurrentHeaderStep ? (
                            <span className="dataset-flow-shell__step-badge">
                              Step {stepNumber}
                            </span>
                          ) : (
                            <span className="dataset-flow-shell__step-marker">
                              {stepNumber}
                            </span>
                          )}
                          <span
                            className={`dataset-flow-shell__step-name ${isCurrentHeaderStep
                              ? "dataset-flow-shell__step-name--active"
                              : ""
                              }`}
                            style={{ color: '#b1c7e8' }}
                          >
                            {stepLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <p className="mb-0 from-shell-sub-text">{getStepDescription(currentStepLabel)}</p>
              </div>
              <div className="px-2">
                <DataConfiguration2 expData={schema} />
              </div>
              <Button className="d-none" />
            </>
          ) : (
          <ErrorScreen message={error || "No form Found"} />
        )}
      </section>


      {uploading && (
        <>
          <div className="text-center">{uploadProgress} %</div>
          <Progress value={uploadProgress} />
        </>
      )}

      {/* FOOTER */}
      {schema && (
        <div className="dataset-footer">
          <div className="dataset-footer__actions">

            {activeStep > 0 ? (
              <button
                className="btn btn-secondary text-white py-2"
                onClick={handleBack}
              >
                {loadingAction === "back" ? <FontAwesomeIcon className="me-2" icon={faSpinner} spin /> : <BackIcon className="wf-icon me-2 text-white" />}
                Back
              </button>
            ) : <div></div>}

            {activeStep === data?.steppers?.length - 1 ? (
              <button
                className="btn btn-primary py-2 fw-bold"
                disabled={loadingAction === "submit"}
                onClick={handleFinalSubmit}
              >
                Submit{loadingAction === "submit" && <FontAwesomeIcon icon={faSpinner} spin />}
              </button>
            ) : (
              <button
                type="submit"
                form="add-data-form"
                className="btn btn-primary py-2 fw-bold"
                onClick={() => setHasTriedSubmit(true)}
                disabled={
                  activeStep === 0 &&
                  formData["Select Data Source"] === "BrowserUpload" &&
                  (!currentFile || uploading)
                }
              >
                {uploading ? "Uploading..." : <>Next {loadingAction === "next" ? <FontAwesomeIcon icon={faSpinner} spin /> : <ArrowRightIcon className="wf-icon text-white" />} </>}
              </button>
            )}
          </div>
        </div>
      )}

    </main>
  );

}
